import React, { useEffect, useState } from 'react';
import { Button, Form, Layout, Select, Skeleton, Space } from 'antd';
import DateRangePicker from 'components/DateRangePicker';
import { getDefaultDateRange } from 'shared/constants/date';
import { PlusCircleFilled } from '@ant-design/icons';
import { gql } from '@apollo/client';
import useCustomer from 'hooks/useCustomer';
import { useMutation, useQuery } from 'hooks/apollo';
import SortableList from 'components/SortableList';
import { SEGMENT_COLORS, STATIC_SEGMENT_COLOR } from 'shared/constants/colors';
import _ from 'lodash';
import EditSegmentDrawer from './EditSegmentDrawer';
import EditGlobalFilterDrawer from './EditGlobalFilterDrawer';
import './global-filter.less';
import GlobalFilterSegment from './GlobalFilterSegment';
import AddSegmentDrawer from './AddSegmentDrawer';

const { Sider } = Layout;

const initialFilter = {
  id: 'new',
  title: 'Unnamed filter',
  dateRangeConfig: getDefaultDateRange(),
  allEnabled: true,
};

const GLOBAL_FILTER_QUERY = gql`
  query GlobalFilters($customerId: ID!) {   
    globalFilters(customerId: $customerId) {
      id
      createdAt      
      title
      allEnabled
      dateRangeConfig {
        ...DateRangeConfig
      }
      segments(orderBy: sortIndex_ASC) {
        id
        title
        enabled
        sortIndex
        query
        color
        dateRangeConfig {
          ...DateRangeConfig
        }
      }
    }
  }
  fragment DateRangeConfig on FilterDateRange {
    id
    period
    periodType
    lastNumberInput
    comparePeriod
    compareToPrevious
    compareToPreviousValue
    specificDates
    compareSpecificDates
  }
`;

const CREATE_GLOBAL_FILTER_MUTATION = gql`
  mutation CreateGlobalFilter($customerId: ID!, $input: GlobalFilterInput!) {
    createGlobalFilter(customerId: $customerId, input: $input) {
      id
    }
  }
`;

const UPDATE_GLOBAL_FILTER_MUTATION = gql`
  mutation UpdateGlobalFilter($id: ID!, $input: GlobalFilterInput!) {
    updateGlobalFilter(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_GLOBAL_FILTER_MUTATION = gql`
  mutation DeleteGlobalFilter($id: ID!) {
    deleteGlobalFilter(id: $id) {
      id
    }
  }
`;

const GlobalFilter = ({ onChange }) => {
  const { customer } = useCustomer();
  const { data, refetch } = useQuery(GLOBAL_FILTER_QUERY, { variables: { customerId: customer?.id }, skip: !customer?.id });
  const [addSegment, setAddSegment] = useState(null);
  const [editSegment, setEditSegment] = useState(null);
  const [editGlobalFilter, setEditGlobalFilter] = useState(false);
  const [selectedGlobalFilterId, setSelectedGlobalFilterId] = useState(null);
  const [form] = Form.useForm();
  const [createGlobalFilter] = useMutation(CREATE_GLOBAL_FILTER_MUTATION);
  const [updateGlobalFilter] = useMutation(UPDATE_GLOBAL_FILTER_MUTATION);
  const [deleteGlobalFilter] = useMutation(DELETE_GLOBAL_FILTER_MUTATION);

  useEffect(() => {
    if (data && !selectedGlobalFilterId) {
      setSelectedGlobalFilterId(data.globalFilters.length ? data.globalFilters[0].id : 'new');
    }
  }, [data, selectedGlobalFilterId]);

  const isNewFilter = selectedGlobalFilterId === 'new';

  const selectedGlobalFilter = data?.globalFilters.find(filter => filter.id === selectedGlobalFilterId) || initialFilter;
  const allSegment = {
    id: 'all',
    title: 'All',
    color: STATIC_SEGMENT_COLOR.dark,
    enabled: selectedGlobalFilter.allEnabled,
  };
  const mainSegment = allSegment.enabled ? allSegment : selectedGlobalFilter?.segments.find(s => s.enabled);

  useEffect(() => {
    if (selectedGlobalFilter) {
      form.resetFields();
      form.setFieldsValue(selectedGlobalFilter);
      onChange({
        mainSegment,
        mainDateRangeConfig: selectedGlobalFilter.dateRangeConfig,
        comparisonSegments: selectedGlobalFilter?.segments?.filter(s => s.enabled && s.id !== mainSegment?.id),
      });
    }
  }, [selectedGlobalFilter]);

  const onSaveFilter = async values => {
    if (isNewFilter) {
      const { data: { createGlobalFilter: { id } } } = await createGlobalFilter({
        variables: {
          customerId: customer.id,
          input: {
            ..._.omit(selectedGlobalFilter, ['id']),
            ...values,
          },
        },
      });
      setSelectedGlobalFilterId(id);
    } else {
      await updateGlobalFilter({
        variables: {
          id: selectedGlobalFilter.id,
          input: values,
        },
      });
    }
    await refetch();
  };

  const onFilterChange = async (changedValues, allValues) => {
    if (changedValues.segments || changedValues.allEnabled || changedValues.dateRangeConfig) {
      onSaveFilter(allValues);
    }
  };

  const onRemoveFilter = async () => {
    await deleteGlobalFilter({ variables: { id: selectedGlobalFilter.id } });
    await refetch();
    setSelectedGlobalFilterId(null);
    setEditGlobalFilter(false);
  };

  const onAddSegment = (values) => {
    const sortIndex = selectedGlobalFilter.segments?.length || 0;
    setAddSegment(false);
    if (values === 'custom') {
      setEditSegment({ sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0] });
    } else if (values === 'custom-date-range') {
      setEditSegment({ sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0], dateRangeConfig: getDefaultDateRange() });
    } else {
      onSaveFilter({ segments: [...(selectedGlobalFilter.segments || []), { ...values, sortIndex, color: SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0] }] });
    }
  };

  const onSaveSegment = (values) => onSaveFilter({ segments: [...(selectedGlobalFilter.segments?.filter(fs => fs.id !== values.id) || []), values] });

  const onRemoveSegment = () => onSaveFilter({ segments: selectedGlobalFilter.segments?.filter(fs => fs.id !== editSegment.id) });

  return (
    <Sider width={300} theme="light" style={{ padding: '8px' }}>
      {data?.globalFilters.length > 0 && (
        <Select
          value={selectedGlobalFilter?.id}
          onChange={setSelectedGlobalFilterId}
          options={[...data.globalFilters.map(filter => ({ label: filter.title, value: filter.id })), {
            label: (
              <Space>
                <PlusCircleFilled />
                New filter
              </Space>
            ),
            value: 'new',
          }]}
          style={{ display: 'block', marginBottom: '16px' }}
        />
      )}
      {selectedGlobalFilter ? (
        <Form layout="vertical" onValuesChange={onFilterChange} onFinish={onSaveFilter} form={form}>
          <Form.Item name="dateRangeConfig" label="Main date range">
            <DateRangePicker allowCompare={false} />
          </Form.Item>
          <Form.Item>
            <GlobalFilterSegment
              mainDateRangeConfig={selectedGlobalFilter.dateRangeConfig}
              mainSegment={mainSegment}
              value={allSegment}
              onChange={({ enabled }) => onSaveFilter({ allEnabled: enabled })}
            />
          </Form.Item>
          <Form.Item label="Segments">
            <Form.List name="segments">
              {(fields, { move }) => (
                <>
                  <SortableList
                    onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                    lockAxis="y"
                  >
                    {fields.map((field, sortIndex) => (
                      <SortableList.Item key={field.key} index={sortIndex}>
                        <Form.Item {...field} noStyle>
                          <GlobalFilterSegment onEdit={setEditSegment} mainSegment={mainSegment} mainDateRangeConfig={selectedGlobalFilter.dateRangeConfig} />
                        </Form.Item>
                      </SortableList.Item>
                    ))}
                  </SortableList>

                  <Button block onClick={() => setAddSegment(true)}>Add Segment</Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Button block onClick={() => setEditGlobalFilter(true)}>Filter settings</Button>
        </Form>
      ) : (
        <Skeleton active />
      )}
      <AddSegmentDrawer open={!!addSegment} segment={addSegment} onSubmit={onAddSegment} onCancel={() => setAddSegment(null)} globalDateRange={selectedGlobalFilter.dateRangeConfig} />
      <EditSegmentDrawer open={!!editSegment} segment={editSegment} onSubmit={onSaveSegment} onRemove={editSegment?.id !== selectedGlobalFilter?.mainSegment?.id && onRemoveSegment} onCancel={() => setEditSegment(null)} globalDateRange={selectedGlobalFilter.dateRangeConfig} />
      <EditGlobalFilterDrawer open={editGlobalFilter} globalFilter={selectedGlobalFilter} onSubmit={onSaveFilter} onRemove={onRemoveFilter} onCancel={() => setEditGlobalFilter(false)} />
    </Sider>
  );
};

export default GlobalFilter;
